import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  OutlinedInput,
  Typography
} from '@mui/material';
import { SelectAll, Person, Group } from '@mui/icons-material';

/**
 * User Filter Dropdown Component
 * Supports single/multiple user selection with "All" option
 */
export const UserFilterDropdown = ({
  users = [],
  selectedUsers = [],
  onUserChange,
  multiple = true,
  label = "Select Agents",
  placeholder = "Choose agents to filter by",
  disabled = false,
  loading = false,
  showAllOption = true,
  sx = {}
}) => {
  // Handle user selection changes
  const handleChange = (event) => {
    const value = event.target.value;

    if (!multiple) {
      onUserChange(value);
      return;
    }

    // Handle multiple selection
    if (typeof value === 'string') {
      onUserChange(value.split(','));
      return;
    }

    // Handle "All" selection
    if (value.includes('all')) {
      // If "All" was just selected, clear all other selections
      if (!selectedUsers.includes('all')) {
        onUserChange(['all']);
      } else {
        // If "All" was deselected, clear all selections
        onUserChange([]);
      }
    } else {
      // Normal multi-select handling
      onUserChange(value);
    }
  };

  // Prepare menu items
  const menuItems = [
    // "All" option
    ...(showAllOption && multiple ? [{
      id: 'all',
      name: 'All Agents',
      isAllOption: true
    }] : []),
    // Individual users
    ...users.map(user => ({
      id: user._id || user.id || user.userId,
      name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User',
      isAllOption: false
    }))
  ];

  // Determine if "All" is selected
  const isAllSelected = selectedUsers.includes('all');

  // Display value for the select input
  const displayValue = multiple
    ? (isAllSelected ? ['all'] : selectedUsers)
    : selectedUsers;

  // Render value function for multiple select
  const renderValue = (selected) => {
    if (!multiple) {
      const selectedUser = menuItems.find(item => item.id === selected);
      return selectedUser ? selectedUser.name : '';
    }

    if (!selected || selected.length === 0) {
      return <Typography variant="body2" color="text.secondary">{placeholder}</Typography>;
    }

    if (selected.includes('all')) {
      return (
        <Chip
          icon={<Group />}
          label="All Agents"
          size="small"
          color="primary"
          variant="filled"
        />
      );
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.slice(0, 2).map((userId) => {
          const user = menuItems.find(item => item.id === userId);
          return user ? (
            <Chip
              key={userId}
              icon={<Person />}
              label={user.name}
              size="small"
              color="primary"
              variant="outlined"
            />
          ) : null;
        })}
        {selected.length > 2 && (
          <Chip
            label={`+${selected.length - 2} more`}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </Box>
    );
  };

  return (
    <FormControl sx={{ minWidth: 250, ...sx }} disabled={disabled}>
      <InputLabel id="user-filter-select-label">{label}</InputLabel>
      <Select
        labelId="user-filter-select-label"
        multiple={multiple}
        value={displayValue}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={renderValue}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2
          }
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              width: 300,
            },
          },
        }}
      >
        {loading ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">Loading users...</Typography>
          </MenuItem>
        ) : menuItems.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">No users available</Typography>
          </MenuItem>
        ) : (
          menuItems.map((item) => (
            <MenuItem
              key={item.id}
              value={item.id}
              sx={{
                backgroundColor: item.isAllOption && isAllSelected ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                fontWeight: item.isAllOption ? 'bold' : 'normal'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {item.isAllOption ? (
                  <SelectAll fontSize="small" />
                ) : (
                  <Person fontSize="small" />
                )}
                {item.name}
                {item.isAllOption && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ({users.length} agents)
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
};
